import { Button, Input, toast } from '@berky/ui'
import { FileUp, X } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'

export function FeedbackFilePicker({
  file,
  onChange,
  disabled,
}: {
  file: File | null
  onChange: (file: File | null) => void
  disabled: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const id = useId()
  const preview = useRef<HTMLImageElement>(null)
  const isImage = !!file && ['image/png', 'image/jpeg', 'image/webp'].includes(file.type)
  useEffect(() => {
    if (!file || !preview.current) return
    const url = URL.createObjectURL(file)
    preview.current.src = url
    return () => URL.revokeObjectURL(url)
  }, [file])

  function select(files: FileList | null) {
    if (disabled || !files?.length) return
    const next = feedbackFileSelection(files)
    if (next) onChange(next)
  }

  return (
    <div
      className="space-y-3 rounded-lg border border-dashed bg-muted/20 p-4"
      onDragOver={(event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
      }}
      onDrop={(event) => {
        event.preventDefault()
        event.stopPropagation()
        select(event.dataTransfer.files)
      }}
    >
      <Input
        ref={input}
        id={id}
        type="file"
        className="sr-only"
        disabled={disabled}
        aria-label="Datei zum Feedback hinzufügen"
        onChange={(event) => {
          select(event.target.files)
          event.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          <FileUp aria-hidden="true" />
          {file ? 'Datei ersetzen' : 'Datei auswählen'}
        </Button>
        <span className="text-sm text-muted-foreground">
          Hier ablegen oder mit Strg+V einfügen · optional · bis 10 MB
        </span>
      </div>
      {file && (
        <div className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 break-all">{file.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            aria-label="Datei entfernen"
            onClick={() => onChange(null)}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
      )}
      {isImage && (
        <img
          ref={preview}
          alt="Vorschau des Dateianhangs"
          className="max-h-64 max-w-full rounded-md object-contain"
        />
      )}
    </div>
  )
}

export function feedbackFileSelection(files: ArrayLike<File>): File | null {
  if (files.length !== 1) {
    toast.error('Bitte fügen Sie genau eine Datei ein.')
    return null
  }
  const file = files[0]
  if (!file || file.size === 0 || file.size > 10 * 1024 * 1024) {
    toast.error('Bitte wählen Sie eine nicht leere Datei bis 10 MB aus.')
    return null
  }
  return file
}

export async function feedbackFileInput(file: File) {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'))
    reader.onabort = () => reject(new Error('Das Einlesen der Datei wurde abgebrochen.'))
    reader.readAsDataURL(file)
  })
  return { fileName: file.name, data }
}
